<script setup>
import {reactive} from '../../../node_modules/vue/dist/vue.esm-bundler';
import {computed} from 'vue';

const props = defineProps({
  interest1Year: Number,
  interest2Year: Number,
  interest3Year: Number,
})

const state = reactive({
  payout: 'jaehrlich',
  amount: 25000,
});

const currencyFormat = Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

function zinseszins(betrag, zins, dauer) {
  return betrag * Math.pow((1 + zins / 100), dauer);
}

const payout1Year = computed(() => {
  if (state.payout === 'jaehrlich') {
    return (state.amount * props.interest1Year / 100) * (1);
  } else {
    return zinseszins(state.amount, props.interest1Year, 1) - state.amount;
  }
});

const payout2Year = computed(() => {
  if (state.payout === 'jaehrlich') {
    return (state.amount * props.interest2Year / 100) * (2);
  } else {
    return zinseszins(state.amount, props.interest2Year, 2) - state.amount;
  }
});

const payout3Year = computed(() => {
  if (state.payout === 'jaehrlich') {
    return (state.amount * props.interest3Year / 100) * (3);
  } else {
    return zinseszins(state.amount, props.interest3Year, 3) - state.amount;
  }
});

const height1Year = computed(() => {
  return payout1Year.value / payout3Year.value * 100 + '%';
});

const height2Year = computed(() => {
  return payout2Year.value / payout3Year.value * 100 + '%';
});

const height3Year = computed(() => {
  return payout3Year.value / payout3Year.value * 100 + '%';
});

</script>

<template>
  <div class="calculator">
    <div class="columns">
      <div class="column is-3 text-flow">
        <label for="anlagebetrag">
            <div class="heading-4">Anlagebetrag</div>
        </label>
        <div class="control has-icons-right">
          <input id="anlagebetrag" class="input is-large js-calculator-value" type="text" placeholder="Schlussrate" v-model="state.amount" aria-controls="calculator-output">
          <span class="icon is-medium is-right">€</span>
        </div>
        <fieldset>
            <legend>
                <p>Möchten Sie eine jährliche Auszahlung des Zinsertrags oder eine Auszahlung am Ende der Laufzeit?</p>
            </legend>
            <div class="field">
            <div class="control">
                <label class="radio">
                <input type="radio" name="auszahlung" value="jaehrlich" v-model="state.payout">
                Jährliche Auszahlung
                </label>
            </div>
            </div>
            <div class="field">
            <div class="control">
                <label class="radio">
                <input type="radio" name="auszahlung" value="ende" v-model="state.payout">
                Am Ende (mit Zinseszins)
                </label>
            </div>
            </div>
        </fieldset>
      </div>
      <div class="column is-9">
        <div id="calculator-output" class="box has-background is-full-height text-flow" aria-live="polite">
          <h3 class="heading-3">Zinsergebnis am Ende der Laufzeit</h3>
          <div class="columns is-hidden-touch">
            <div class="column is-4 text-flow">
              <div class="bar has-text-centered">
                <div class="bar-background" :style="`height: ${height1Year}`" :aria-label="`Ergebnis bei einer Verzinsung von ${interest1Year} % p. a. und einem Jahr Laufzeit`"><span>{{ currencyFormat.format(payout1Year) }}</span></div>
                <div class="bar-title" aria-hidden="true">1 Jahr</div>
                <div class="bar-suffix" aria-hidden="true">{{ interest1Year }} % p. a.</div>
              </div>
              <!-- <div class="buttons is-centered"><a :href="'https://ebanking.opel-direktbank.de/eBankingAccOpen/pages/step_01.jsf?produkt=02&laufzeit=12&betrag=' + state.amount + '&za=1'" class="button is-primary">Jetzt eröffnen</a></div>-->
            </div>
            <div class="column is-4 text-flow">
              <div class="bar has-text-centered">
                <div class="bar-background" :style="`height: ${height2Year}`" :aria-label="`Ergebnis bei einer Verzinsung von ${interest2Year} % p. a. und zwei Jahren Laufzeit`"><span>{{ currencyFormat.format(payout2Year) }}</span></div>
                <div class="bar-title" aria-hidden="true">2 Jahre</div>
                <div class="bar-suffix" aria-hidden="true">{{ interest2Year }} % p. a.</div>
              </div>
              <!-- <div class="buttons is-centered"><a :href="'https://ebanking.opel-direktbank.de/eBankingAccOpen/pages/step_01.jsf?produkt=02&laufzeit=24&betrag=' + state.amount + '&za=1'" class="button is-primary">Jetzt eröffnen</a></div>-->
            </div>
            <div class="column is-4 text-flow">
              <div class="bar has-text-centered">
                <div class="bar-background" :style="`height: ${height3Year}`" :aria-label="`Ergebnis bei einer Verzinsung von ${interest3Year} % p. a. und drei Jahren Laufzeit`"><span class="is-inner">{{ currencyFormat.format(payout3Year) }}</span></div>
                <div class="bar-title" aria-hidden="true">3 Jahre</div>
                <div class="bar-suffix" aria-hidden="true">{{ interest3Year }} % p. a.</div>
              </div>
              <!-- <div class="buttons is-centered"><a :href="'https://ebanking.opel-direktbank.de/eBankingAccOpen/pages/step_01.jsf?produkt=02&laufzeit=36&betrag=' + state.amount + '&za=1'" class="button is-primary">Jetzt eröffnen</a></div>-->
            </div>
          </div>

          <div class="columns is-hidden-desktop">
            <div class="column">
              <table class="table is-fullwidth is-calculator">
                <thead>
                <tr>
                  <th>Laufzeit</th>
                  <th>Zinssatz</th>
                  <th>Zins&shy;ergebnis</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>1 Jahr</td>
                  <td style="white-space: nowrap">{{ interest1Year }} % p. a.</td>
                  <td>{{ currencyFormat.format(payout1Year) }}</td>
                </tr>
                <tr>
                  <td>2 Jahre</td>
                  <td style="white-space: nowrap">{{ interest2Year }} % p. a.</td>
                  <td>{{ currencyFormat.format(payout2Year) }}</td>
                </tr>
                <tr>
                  <td>3 Jahre</td>
                  <td style="white-space: nowrap">{{ interest3Year }} % p. a.</td>
                  <td>{{ currencyFormat.format(payout3Year) }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

</template>
